// Reel advance — the step-to-step hand-off inside the start flow.
//
// One opaque panel sweeps the frame in a single continuous direction, hot
// frame-line leading. If the caller supplied both a `from` and `to` slot, a
// carried label rides across with it — the hand-off that makes the chosen thing
// visibly travel to where it now lives (the hero's name moving from the card the
// player picked to the draft header that names it).
//
// Painted imperatively off the shared clock; see TapeTransportRenderer for why.

import { useRef } from 'react';
import { useTransitionStore } from '../../stores/transition';
import type { AdvanceScore } from '../../theme/advanceTimeline';
import { beatEnd, sampleTrack, type Stop } from '../../theme/sequence';
import { easeFn } from '../../theme/motion';
import { colors } from '../../theme/tokens';
import { useSequenceFrame } from './clock';

export function AdvanceRenderer({ score }: { score: AdvanceScore }) {
  const panel = useRef<HTMLDivElement | null>(null);
  const label = useRef<HTMLDivElement | null>(null);

  const tracks = useRef<Tracks | null>(null);
  if (!tracks.current || tracks.current.forTotal !== score.total) {
    tracks.current = buildTracks(score);
  }

  const { carry } = useTransitionStore.getState().payload;

  useSequenceFrame((elapsed) => {
    const tr = tracks.current;
    if (!tr) return;

    // Sweep position, 0 → 1 → 2: off-screen, covering, off the other side. One
    // continuous direction, which is what makes it read as film advancing
    // rather than a door opening and closing.
    const p = sampleTrack(tr.sweep, elapsed);

    if (panel.current) {
      if (score.travels) {
        const pct = (p - 1) * 100 * score.direction;
        panel.current.style.transform = `translateX(${pct}%)`;
        panel.current.style.opacity = '1';
      } else {
        panel.current.style.transform = 'translateX(0%)';
        panel.current.style.opacity = String(sampleTrack(tr.dissolve, elapsed));
      }
    }

    if (label.current) {
      if (!carry) {
        label.current.style.opacity = '0';
        return;
      }

      const { slots } = useTransitionStore.getState();
      const { from, to } = slots;
      const box = label.current.getBoundingClientRect();
      const t = sampleTrack(tr.carry, elapsed);

      let x: number;
      let y: number;

      if (from && to && score.travels) {
        // A real hand-off: the label travels from the thing the player chose to
        // where it now lives.
        const fx = from.x + from.width / 2 - box.width / 2;
        const fy = from.y + from.height / 2 - box.height / 2;
        const tx = to.x + to.width / 2 - box.width / 2;
        const ty = to.y + to.height / 2 - box.height / 2;
        x = fx + (tx - fx) * t;
        y = fy + (ty - fy) * t;
      } else if (to) {
        // Destination known but no journey (reduced motion) — resolve in place.
        x = to.x + to.width / 2 - box.width / 2;
        y = to.y + to.height / 2 - box.height / 2;
      } else {
        // No slots at all. Rather than discard the caller's label, show it
        // centred as a brief caption on the covered frame — which is how the
        // /play → hero and draft → tavern steps announce what comes next.
        x = window.innerWidth / 2 - box.width / 2;
        y = window.innerHeight / 2 - box.height / 2;
      }

      label.current.style.transform = `translate(${x}px, ${y}px)`;
      label.current.style.opacity = String(sampleTrack(tr.carryOpacity, elapsed));
    }
  });

  return (
    <>
      <div
        ref={panel}
        className="absolute inset-0"
        style={{ backgroundColor: colors.void, transform: 'translateX(-100%)' }}
      >
        {/* Frame line — the hot edge of the advancing frame. Sits on whichever
            side is leading. */}
        <div
          className="absolute inset-y-0 w-[3px]"
          style={{
            [score.direction === 1 ? 'left' : 'right']: 0,
            background: `linear-gradient(180deg, transparent, ${colors.sodiumGlow}, transparent)`,
          }}
        />
      </div>

      {/* The carried label. Only rendered when the caller gave us something to
          carry AND both ends of the journey are known. */}
      <div
        ref={label}
        className="absolute left-0 top-0 whitespace-nowrap font-cholo text-[20px] leading-none text-sodium md:text-[30px]"
        style={{ opacity: 0 }}
      >
        {carry ?? ''}
      </div>
    </>
  );
}

interface Tracks {
  forTotal: number;
  sweep: Stop[];
  dissolve: Stop[];
  carry: Stop[];
  carryOpacity: Stop[];
}

function buildTracks(s: AdvanceScore): Tracks {
  const shut = beatEnd(s.cover);
  return {
    forTotal: s.total,
    // 0 = fully off the incoming side, 1 = covering, 2 = fully off the far side.
    sweep: [
      { at: 0, v: 0 },
      { at: shut, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: s.total, v: 2, ease: easeFn.in },
    ],
    dissolve: [
      { at: 0, v: 0 },
      { at: shut, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: s.total, v: 0, ease: easeFn.out },
    ],
    // The label crosses entirely within the covered window, so it is never seen
    // overlapping either screen's own copy of the same text.
    carry: [
      { at: 0, v: 0 },
      { at: shut, v: 0 },
      { at: s.settle.at, v: 1, ease: easeFn.transport },
    ],
    carryOpacity: [
      { at: 0, v: 0 },
      { at: shut, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: s.settle.at + s.settle.dur * 0.6, v: 0, ease: easeFn.in },
    ],
  };
}
